import { uuid } from './uuid';
import { hasConsent } from './cookie-consent';

/*
 * Cookie consent (`features.cookieConsent`, MiLicita). Everything this module
 * stores is analytics or advertising memory, so on such a deployment it is
 * written and read only under the matching grant — see lib/cookie-consent.ts.
 * Where consent is not required (UZ/KZ) both predicates are constant `true` and
 * nothing below changes.
 */
/** `tenders_vid` is the join key of the funnel, PostHog AND Meta: either grant keeps it. */
function visitorIdAllowed(): boolean {
  return hasConsent('analytics') || hasConsent('marketing');
}
/** First-touch UTM / referrer memory: attribution, analytics or advertising. */
const attributionAllowed = visitorIdAllowed;
// _fbp / _fbc cookies follow the shape fb.{version}.{timestamp}.{payload}.
const FB_COOKIE_REGEX = /^fb\.\d+\.\d+\..+$/;

const FBCLID_SESSION_KEY = 'tenders_fbclid';
/**
 * fbclid + the moment we first saw it, kept in localStorage. sessionStorage —
 * where this used to live alone — dies with the tab, so an ad click that
 * converted in a later session lost its Click ID entirely and the CAPI event
 * went out without `fbc`. Meta's own `_fbc` cookie lasts 90 days; this mirrors
 * that, and matters most for the users whose blocked Pixel never wrote the
 * cookie in the first place.
 */
const FBCLID_STORE_KEY = 'tenders_fbclid_v2';
/** Matches the lifetime of Meta's own _fbc cookie. */
const FBCLID_TTL_MS = 90 * 24 * 60 * 60 * 1000;

interface StoredClickId {
  fbclid: string;
  /** When the click was observed — becomes the timestamp inside `fbc`. */
  ts: number;
}

/** Read the stored click id, dropping it once it ages past Meta's window. */
function readStoredClickId(): StoredClickId | undefined {
  try {
    const raw = localStorage.getItem(FBCLID_STORE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<StoredClickId>;
      if (typeof parsed?.fbclid === 'string' && typeof parsed.ts === 'number') {
        if (Date.now() - parsed.ts < FBCLID_TTL_MS) return { fbclid: parsed.fbclid, ts: parsed.ts };
        localStorage.removeItem(FBCLID_STORE_KEY);
        return undefined;
      }
    }
    // Fall back to the old session-scoped value so a visitor mid-journey at
    // release time keeps their click id. No timestamp was stored back then.
    const legacy = sessionStorage.getItem(FBCLID_SESSION_KEY);
    if (legacy) return { fbclid: legacy, ts: Date.now() };
  } catch {
    // storage unavailable (private mode) — treat as no click id.
  }
  return undefined;
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

type UtmKey = (typeof UTM_KEYS)[number];

/**
 * UTM tags and the first-touch referrer share Meta's 90-day click window, and for
 * the same reason `fbc` does: an ad click routinely converts in a LATER session
 * (a new tab, the next day), and the campaign that first brought a visitor should
 * still be creditable when they finally sign up. They used to live in
 * sessionStorage — which dies with the tab — so `fbc` survived a return visit
 * while its UTMs did not, and prod showed the scar: `fbc` on 25.6% of users,
 * `utm_source` on 4.5%, a 5.7× gap that was storage lifetime, not missing tags.
 *
 * Durable AND first-touch: only the FIRST value seen inside the window is kept,
 * so a fresh campaign weeks later cannot overwrite the acquisition that actually
 * happened — the concern the old session-scoping addressed, preserved here by
 * the write-once rule instead of by throwing the value away. The server's
 * `rememberAttribution` is first-touch-immutable too, so the two agree.
 */
const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;

interface Stamped {
  value: string;
  ts: number;
}

/** Read a `{value, ts}` entry, dropping it once it ages past the 90-day window. */
function readStamped(key: string): string | undefined {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<Stamped>;
    if (typeof parsed?.value === 'string' && typeof parsed.ts === 'number') {
      if (Date.now() - parsed.ts < ATTRIBUTION_TTL_MS) return parsed.value;
      localStorage.removeItem(key);
    }
  } catch {
    // storage unavailable / malformed — treat as absent.
  }
  return undefined;
}

/** Write once — first touch wins. A later value inside the window is ignored. */
function writeStampedFirstTouch(key: string, value: string): void {
  try {
    if (readStamped(key) !== undefined) return;
    localStorage.setItem(key, JSON.stringify({ value, ts: Date.now() } satisfies Stamped));
  } catch {
    // storage unavailable (private mode) — nothing to persist.
  }
}

/** localStorage key holding the durable `{value, ts}` for one UTM param. */
const utmStoreKey = (key: UtmKey): string => `tenders_utm_${key}_v2`;
/** localStorage key holding the durable first-touch referrer `{value, ts}`. */
const REFERRER_STORE_KEY_V2 = 'tenders_referrer_v2';

/**
 * The Google Ads click id, first-touch, 90 days — `{value, ts}` like the UTMs.
 *
 * Auto-tagging is on in the Ads account, so every ad click lands with
 * `?gclid=…` (or, from iOS apps, `?gbraid=…` / `?wbraid=…`). It is the only key
 * an offline conversion upload can attribute a purchase with, and until
 * 2026-10-09 it was read by gtag.js and then lost: `first_touch_landing` is
 * stored without its query. One value, as it came: `gclid` bare, the two iOS
 * variants prefixed `gbraid:` / `wbraid:` so the upload can tell which field of
 * the Google Ads API it belongs to. First-touch-wins, unlike `fbclid` (which a
 * fresh click refreshes): the first ad click is the one that brought the
 * visitor, and the server stores it once, on the same rule.
 */
const GCLID_STORE_KEY = 'tenders_gclid_v1';

/** The Google click id in this URL, if any — `gclid` first, then the iOS pair. */
function googleClickIdFrom(params: URLSearchParams): string | undefined {
  const gclid = params.get('gclid');
  if (gclid) return gclid;
  const gbraid = params.get('gbraid');
  if (gbraid) return `gbraid:${gbraid}`;
  const wbraid = params.get('wbraid');
  if (wbraid) return `wbraid:${wbraid}`;
  return undefined;
}

/**
 * Durable per-visitor id, used as Meta `external_id`. A `_fbp` cookie only
 * identifies a browser and, post-third-party-cookie, no longer resolves to a
 * Meta user — so anonymous pixel events never populate a Website Custom
 * Audience. A stable external_id we mint ourselves gives Meta an identity to
 * match on. It rides on BOTH the browser Pixel (advanced matching) and the CAPI
 * under the same value, so the two legs agree. See
 * docs/explanation/meta-website-audience-empty.md.
 */
const VISITOR_ID_KEY = 'tenders_vid';

/**
 * Read (or lazily create) the anonymous visitor id. localStorage-backed so it
 * persists across sessions; returns undefined only when storage is unavailable
 * (private mode / SSR), in which case events simply go without external_id.
 */
export function getOrCreateVisitorId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  if (!visitorIdAllowed()) return undefined;
  try {
    let id = localStorage.getItem(VISITOR_ID_KEY);
    if (!id) {
      id = uuid();
      localStorage.setItem(VISITOR_ID_KEY, id);
    }
    return id;
  } catch {
    return undefined;
  }
}

/**
 * Mint a NEW visitor id, discarding the current one. Call on logout.
 *
 * This id is the join key for all three analytics systems at once — PostHog's
 * distinct id, Meta's `external_id`, and the DIY funnel's `anon_id` — so it
 * must never outlive the person it identifies. It used to: `posthog.reset()`
 * cleared PostHog's own storage while `tenders_vid` survived in localStorage,
 * so a shared browser (office machine, family device) walked this path —
 *
 *   user A logs in   → vid V merged into person A
 *   user A logs out  → V survives
 *   user B logs in   → identify(B, $anon_distinct_id: V)
 *
 * — and two real people became one PostHog person. That merge is irreversible.
 * The same id going to Meta as `external_id` would likewise conflate them in a
 * Custom Audience, and `visitor_links` (visitor_id is its PRIMARY KEY) would
 * keep whichever user got there first and silently attribute the other's
 * events to them.
 *
 * Rotating costs nothing real: after logout we genuinely do not know who this
 * browser is, and on the next login `identify()` re-attaches the person.
 */
export function rotateVisitorId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  if (!visitorIdAllowed()) return undefined;
  try {
    const id = uuid();
    localStorage.setItem(VISITOR_ID_KEY, id);
    return id;
  } catch {
    return undefined;
  }
}

/** The Meta tracking context sent with conversion requests (register, checkout). */
export interface MetaContext {
  fbp?: string;
  fbc?: string;
  /**
   * Anonymous visitor id → Meta `external_id`. The server prefers the real
   * (hashed) user id when the request is authenticated and falls back to this
   * for anonymous events.
   */
  externalId?: string;
  eventSourceUrl?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  /** First-touch external referrer of THIS session — the host the visitor
   *  arrived from. Persisted first-touch on the user at signup so acquisition
   *  channel is visible even when the campaign sent no UTM tags. */
  referrer?: string;
  /**
   * First-touch Google Ads click id (`gclid`, or `gbraid:…` / `wbraid:…`), see
   * GCLID_STORE_KEY. Not a Meta field — it rides in this context because this is
   * the one bundle every signup, login and checkout already sends; the server
   * stores it on the account and the order and sends it nowhere (yet).
   */
  gclid?: string;
}

/** sessionStorage key for the first-touch external referrer of the session. */
const REFERRER_STORE_KEY = 'tenders_referrer';

/**
 * Capture fbclid + UTM params from the landing URL so they survive the journey
 * to the signup/checkout step. Safe to call on any load.
 *
 * The click id, the UTM tags and the referrer are all stored durably (90 days,
 * first-touch-wins) because ad clicks routinely convert in a later session — see
 * ATTRIBUTION_TTL_MS for why UTMs are no longer session-scoped.
 */
export function captureAdClickIds(search?: string): void {
  if (typeof window === 'undefined') return;
  // Cookie consent: nothing is remembered before a grant. `search` lets the
  // consent banner replay the LANDING query (kept in memory by index.html) once
  // the visitor grants — by then the router has usually stripped ?fbclid.
  const marketing = hasConsent('marketing');
  if (!marketing && !attributionAllowed()) return;
  try {
    const params = new URLSearchParams(search ?? window.location.search);
    const fbclid = marketing ? params.get('fbclid') : null;
    if (fbclid) {
      // Stamp the click time now — `fbc` must carry when the click happened,
      // not when the conversion later fired.
      const entry: StoredClickId = { fbclid, ts: Date.now() };
      localStorage.setItem(FBCLID_STORE_KEY, JSON.stringify(entry));
      sessionStorage.setItem(FBCLID_SESSION_KEY, fbclid);
    }
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      // First-touch: the first campaign tag seen inside the 90-day window sticks,
      // and survives a new tab / next-day signup instead of dying with the tab.
      if (value) writeStampedFirstTouch(utmStoreKey(key), value);
    }
    // Google Ads click id — same durable first-touch rule as the UTMs.
    const googleClickId = marketing ? googleClickIdFrom(params) : undefined;
    if (googleClickId) writeStampedFirstTouch(GCLID_STORE_KEY, googleClickId);
    // First-touch external referrer — the "where they came from" signal that
    // survives to signup even when the ad/link carried no UTM tags. Durable and
    // first-touch (writeStampedFirstTouch keeps the first), so an internal
    // navigation (a same-host referrer) never overwrites the real origin.
    if (document.referrer) {
      try {
        const host = new URL(document.referrer).host;
        if (host && host !== window.location.host) {
          writeStampedFirstTouch(REFERRER_STORE_KEY_V2, document.referrer);
        }
      } catch {
        // malformed referrer — ignore.
      }
    }
  } catch {
    // storage unavailable (private mode / SSR) — ignore.
  }
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  for (const cookie of document.cookie.split('; ')) {
    if (cookie.startsWith(`${name}=`)) return cookie.slice(name.length + 1);
  }
  return undefined;
}

function readStored(key: UtmKey): string | undefined {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get(key);
    if (fromUrl) return fromUrl;
    // Durable localStorage value first; fall back to the old session-scoped key
    // for a visitor who captured it under the previous release.
    return readStamped(utmStoreKey(key)) ?? sessionStorage.getItem(`tenders_${key}`) ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Assemble the Meta tracking context from cookies, URL, and storage. The `_fbc`
 * cookie is reconstructed from the stored fbclid when an ad blocker stopped the
 * Pixel from writing it — which, given how much of our traffic blocks the Pixel,
 * is the path that actually carries most Click IDs to the CAPI.
 */
export function getMetaContext(): MetaContext {
  if (typeof window === 'undefined') return {};
  // Cookie consent: without a grant the context carries no identifier at all —
  // only the address the event happened on. Constant `true` on UZ/KZ.
  if (!hasConsent('marketing')) {
    if (!attributionAllowed()) return { eventSourceUrl: window.location.href };
    return {
      externalId: getOrCreateVisitorId(),
      eventSourceUrl: window.location.href,
      utm_source: readStored('utm_source'),
      utm_medium: readStored('utm_medium'),
      utm_campaign: readStored('utm_campaign'),
      utm_term: readStored('utm_term'),
      utm_content: readStored('utm_content'),
      referrer: readReferrer(),
    };
  }

  const fbpRaw = readCookie('_fbp');
  const fbp = fbpRaw && FB_COOKIE_REGEX.test(fbpRaw) ? fbpRaw : undefined;

  // Meta's own cookie wins when present — it is authoritative.
  let fbc = readCookie('_fbc');
  if (fbc && !FB_COOKIE_REGEX.test(fbc)) fbc = undefined;
  if (!fbc) {
    let fbclid: string | undefined;
    let clickedAt = Date.now();
    try {
      const fromUrl = new URLSearchParams(window.location.search).get('fbclid');
      if (fromUrl) {
        fbclid = fromUrl;
      } else {
        const stored = readStoredClickId();
        if (stored) {
          fbclid = stored.fbclid;
          // The timestamp inside fbc must be when the CLICK happened. Stamping
          // "now" would misdate every conversion that lands in a later session.
          clickedAt = stored.ts;
        }
      }
    } catch {
      fbclid = undefined;
    }
    if (fbclid) fbc = `fb.1.${clickedAt}.${fbclid}`;
  }

  return {
    fbp,
    fbc,
    externalId: getOrCreateVisitorId(),
    eventSourceUrl: window.location.href,
    utm_source: readStored('utm_source'),
    utm_medium: readStored('utm_medium'),
    utm_campaign: readStored('utm_campaign'),
    utm_term: readStored('utm_term'),
    utm_content: readStored('utm_content'),
    referrer: readReferrer(),
    gclid: readGoogleClickId(),
  };
}

/**
 * The stored first-touch Google click id; the current URL only when storage is
 * unavailable (private mode), so a same-page signup still carries it.
 */
function readGoogleClickId(): string | undefined {
  const stored = readStamped(GCLID_STORE_KEY);
  if (stored) return stored;
  try {
    return googleClickIdFrom(new URLSearchParams(window.location.search));
  } catch {
    return undefined;
  }
}

/** The durable first-touch external referrer, if any. */
function readReferrer(): string | undefined {
  try {
    // Durable localStorage value first; fall back to the old session-scoped key
    // for a visitor who captured it under the previous release.
    return (
      readStamped(REFERRER_STORE_KEY_V2) ?? sessionStorage.getItem(REFERRER_STORE_KEY) ?? undefined
    );
  } catch {
    return undefined;
  }
}
